import {
	Button,
	chakra,
	Container,
	useMultiStyleConfig,
} from "@chakra-ui/react";
import React from "react";
import { BlogListItemFragment } from "src/__generated__/cms-schema.codegen";
import { BlogpostTeaser } from "src/components/Blogpost/BlogpostTeaser";
import {
	StructuredText,
	StructuredTextData,
} from "src/components/StructuredText/StructuredText";
import {
	TopicSelector,
	useTopicSelector,
} from "src/components/TopicSelector/TopicSelector";
import { useTranslatedString } from "src/i18n/hooks";
import { ArrowRight } from "src/icons";
import { usePagination } from "src/lib/hooks";

type BlogpostSectionProps = {
	topicSelector: boolean;
	topicSelectorText: string | null;
	intro: StructuredTextData | null;
	blogposts: Array<BlogListItemFragment>;
};

export const BlogpostSection: React.FC<BlogpostSectionProps> = ({
	topicSelector: hasTopicSelector,
	topicSelectorText,
	intro,
	blogposts,
}) => {
	const styles = useMultiStyleConfig("BlogpostsSection");
	const t = useTranslatedString();

	const { allTopics, currentTopic, setCurrentTopic } = useTopicSelector(
		blogposts
			.flatMap(({ productFamilies }) => productFamilies)
			.map(
				({
					name,
					slug,
					color: { hex },
				}) => ({
					name,
					slug,
					color: hex,
				})
			)
	);

	const blogpostsSorted = [...blogposts].sort((a, b) =>
		(b.date as string).localeCompare(a.date as string),
	);

	const blogpostsFiltered = blogpostsSorted.filter((b) =>
		currentTopic === t("blogpost.allTopics.slug")
			? b
			: b.productFamilies.some(family => family.slug === currentTopic),
	);

	const { hasMore, onShowNext, onShowLess, sliceItems } = usePagination({
		itemsPerPage: 3,
		itemsTotal: blogpostsFiltered.length,
		breakpoint: "lg",
		overwriteMobileRule: true,
		cutoffThreshold: 0,
	});

	if (!blogposts.length) {
		return null;
	}

	return (
		<chakra.div bg="brand.lighter">
			<Container sx={styles.wrapper}>
				{intro && (
					<chakra.div __css={styles.intro}>
						<StructuredText data={intro} />
					</chakra.div>
				)}

				<>
					{allTopics.length > 2 && hasTopicSelector && (
						<TopicSelector
							topicSelectorText={
								topicSelectorText ?? t("blogpost.chooseTopic")
							}
							currentTopic={currentTopic}
							setCurrentTopic={setCurrentTopic}
							allTopics={allTopics}
						/>
					)}

					<chakra.div sx={styles.blogpostsWrapper}>
						{blogpostsFiltered
							.slice(0, sliceItems)
							.map((blogpost) => (
								<BlogpostTeaser
									key={blogpost.id}
									blogpost={blogpost}
								/>
							))}
					</chakra.div>
				</>

				{hasMore ? (
					<Button
						variant="link"
						fontWeight="500"
						id="show_more_posts"
						onClick={onShowNext}
						rightIcon={<ArrowRight transform="rotate(90deg)" />}
					>
						{t("blogpost.seeMore")}
					</Button>
				) : (
					<Button
						variant="link"
						fontWeight="500"
						id="show_less_posts"
						onClick={onShowLess}
						rightIcon={<ArrowRight transform="rotate(-90deg)" />}
					>
						{t("blogpost.seeLess")}
					</Button>
				)}
			</Container>
		</chakra.div>
	);
};

// 🔬 TBD: Please evaluate
